import { AppState } from '../../types';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePathBasedAppCustomization } from '../../hooks/usePathBasedAppCustomization';
import { useCartMode } from '../../lib/cartModes';
import ReferralBanner from '../ReferralBanner/ReferralBanner';
import styles from './AppBar.module.scss';
import ShopPromotionBanner from '../ShopPromotionBanner';
import NavBar from './NavBar';
import { useLocation } from 'react-router-dom';
import { getFiGrowthBook } from '../../lib/growthbook';

// Evaluate the unified-checkout flag once here and pass it to NavBar as a prop.
// Previously both AppBar and NavBar called getFeatureValue('unified-checkout') independently,
// but the ecom-mini-pdp evaluation in NavBar (which passes userAttributes) calls setAttributes()
// and resets GrowthBook's internal tracking deduplication — causing duplicate exposure events.
export default function AppBar() {
  const { showPromotionBanner } = usePathBasedAppCustomization();
  const [navBarOpacity, setNavBarOpacity] = useState<number>(1);
  const [canHideNavBar, setCanHideNavBar] = useState<boolean>(true);
  const { cartSelector } = useCartMode();
  const cart = useSelector(cartSelector);
  const showPromoBannerImprovements = useSelector(
    (state: AppState) => !!state.config.siteConfig.enableEcommPromoBannerImprovements,
  );
  const { pathname } = useLocation();
  const useUnifiedCheckout = getFiGrowthBook().getFeatureValue('unified-checkout', false);

  // The S3 PDP has a custom nav bar, so we don't want to render the default nav bar on that page
  // Only render nav bar on the supplements page if we're not surfacing the subscribe and save iteration
  // of the supplements PDP.
  const renderNavBar = !(
    pathname.includes('products/smart-collar-v3') ||
    pathname.includes('gift-fi') ||
    pathname.includes('redeem') ||
    pathname.includes('vip') ||
    pathname.includes('supplements') ||
    pathname.includes('fi-mini') ||
    pathname.includes('fi-ultra')
  );

  useEffect(() => {
    // Fade the nav bar out as the user scrolls down so only the promo banner stays pinned.
    //
    // Once the nav bar fades to opacity 0 it collapses to height:0 (see .nav-bar-hidden in
    // AppBar.module.scss). Because .navBarContainer is `position: sticky` and in document flow,
    // that collapse removes the nav bar's height (96px desktop / 80px mobile) from above the
    // viewport, and scroll-anchoring browsers (Chrome/Firefox) compensate by rewriting scrollTop
    // *down* by that same amount. With a single threshold - or a dead band narrower than the nav
    // bar height - that rewrite lands past the toggle point and re-shows the nav bar, which grows
    // the layout back and bumps scrollTop up again: an oscillating loop that flashes the nav bar.
    //
    // Fix: keep the show/hide dead band wider than the collapsed height (here ~160px vs 96px max),
    // so the anchoring rebound always settles *inside* the band and never crosses a threshold.
    // Anchoring then keeps content visually stable (no jump) while the loop is broken. rAF batches
    // the work to at most once per frame regardless of scroll event volume.
    const hideBelow = 180;
    const showBelow = 20;
    let frame: number | null = null;

    const update = () => {
      frame = null;
      const winScroll = document.documentElement.scrollTop || document.body.scrollTop;
      setNavBarOpacity((prev) => {
        if (prev <= 0) {
          // stay hidden until scrolled back near the top
          return winScroll <= showBelow ? 1 : 0;
        }
        const newOpacity = 1 - winScroll / hideBelow;
        return newOpacity <= 0 ? 0 : newOpacity;
      });
    };

    const listenToScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', listenToScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', listenToScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return (
    <>
      <div className={classNames(styles.navBarContainer)}>
        {!showPromoBannerImprovements && showPromotionBanner && !cart.referralCode && (
          <header className={classNames(styles.promotion, 'promotionBanner')}>
            <>
              <ShopPromotionBanner />
            </>
          </header>
        )}
        {cart.referralCode && (
          <header className={classNames(styles.promotion, 'promotionBanner', 'referralBanner')}>
            <ReferralBanner />
          </header>
        )}
        {showPromoBannerImprovements && showPromotionBanner && (
          <header className={classNames(styles.promotion, 'promotionBanner')}>
            <>
              <ShopPromotionBanner
                showBannerOnApply={true}
                bannerShownCallback={(shown) =>
                  // If promo banner is shown, we can hide the nav bar, otherwise we can't
                  setCanHideNavBar(shown)
                }
              />
            </>
          </header>
        )}
        {renderNavBar && (
          <NavBar
            navBarOpacity={
              showPromoBannerImprovements &&
              showPromotionBanner &&
              canHideNavBar &&
              !(useUnifiedCheckout && pathname.includes('checkout'))
                ? navBarOpacity
                : undefined
            }
            useUnifiedCheckout={useUnifiedCheckout}
          />
        )}
      </div>
    </>
  );
}
